import { createContext, useState, useCallback, ReactNode } from "react";
import api from "../../api/api";
import { ApiResponse, PaginationInfo } from "../../types/api";

export const DailyNotesContext = createContext<any>(undefined);

interface DailyNotesProviderProps {
  children: ReactNode;
}

const toNoteApiPayload = (note: any) => ({
  projectId: note.projectId || note.project_id || null,
  user: note.user || note.created_by || "",
  work: note.work || note.category || "",
  noteTitle: note.noteTitle || note.note_title || note.title || "",
  noteDate: note.noteDate || note.note_date || "",
  description: note.description || note.content || "",
  image: note.image || "",
  location: note.location || note.tags || "",
  status: note.status || "Active",
});

const DailyNotesProvider = ({ children }: DailyNotesProviderProps) => {
  const [notes, setNotes] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch notes from the API with pagination
  const fetchNotes = useCallback(async (
    projectId?: number | null,
    page: number = 1,
    perPage: number = 15
  ) => {
    setLoading(true);
    setError(null);
    try {
      let url = '/daily-notes';
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      if (projectId) {
        params.append('project_id', projectId.toString());
      }
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get<any>(url);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let notesData: any[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          notesData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          notesData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          notesData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        notesData = responseData.data;
      } else if (Array.isArray(responseData)) {
        notesData = responseData;
      }
      
      setNotes(notesData);
      setPagination(paginationData);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to fetch notes");
      console.error("Error fetching notes:", err);
      setNotes([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new note
  const addNote = useCallback(async (note: any) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post<ApiResponse<any>>("/daily-notes", toNoteApiPayload(note));
      const newNote = response.data?.data || response.data;
      setNotes((prev) => [...prev, newNote]);
      return newNote;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Edit an existing note
  const editNote = useCallback(async (note: any) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put<ApiResponse<any>>(`/daily-notes/${note.id}`, toNoteApiPayload(note));
      const updatedNote = response.data?.data || response.data;
      setNotes((prev) =>
        prev.map((item) => (item.id === note.id ? updatedNote : item))
      );
      return updatedNote;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a note
  const deleteNote = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/daily-notes/${id}`);
      setNotes((prev) => prev.filter((item) => item.id !== id));
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  // REMOVED auto-fetch useEffect - components will call fetchNotes explicitly

  return (
    <DailyNotesContext.Provider
      value={{
        notes,
        pagination,
        loading,
        error,
        fetchNotes,
        addNote,
        editNote,
        deleteNote,
      }}
    >
      {children}
    </DailyNotesContext.Provider>
  );
};

export default DailyNotesProvider;
